/**
 * Heuristic per-cell bounding-box for the Sibit Stundenliste form.
 *
 * Calibrated against Glückshofer Feb-2026 at 100 DPI by the TesseractEngine
 * (see ocr/engines/tesseract_engine.py). Same coordinates work for the
 * preview PNG since percentages are DPI-independent.
 *
 * Layout assumption: standard Sibit timesheet template with
 *   - 12.7% top margin (header)
 *   - 81.3% bottom edge (rest is summe + footer)
 *   - 31 day-rows of equal height
 *   - 4 time columns (von1/bis1/von2/bis2) + status symbol column
 */

export type FieldName = "von1" | "bis1" | "von2" | "bis2" | "symb";

const ROW_TOP_PCT = 12.7;
const ROW_BOTTOM_PCT = 81.3;
const ROW_HEIGHT_PCT = (ROW_BOTTOM_PCT - ROW_TOP_PCT) / 31;

const COL_RANGES: Record<FieldName, [number, number]> = {
  von1: [16.0, 22.5],
  bis1: [22.5, 28.0],
  von2: [28.0, 33.5],
  bis2: [33.5, 42.0],
  symb: [45.0, 55.0],
};

export interface BboxPct {
  topPct: number;
  leftPct: number;
  widthPct: number;
  heightPct: number;
}

export function cellBboxPct(day: number, field: FieldName): BboxPct | null {
  if (day < 1 || day > 31) return null;
  const range = COL_RANGES[field];
  if (!range) return null;
  return {
    topPct: ROW_TOP_PCT + (day - 1) * ROW_HEIGHT_PCT,
    leftPct: range[0],
    widthPct: range[1] - range[0],
    heightPct: ROW_HEIGHT_PCT,
  };
}

export function rowBboxPct(day: number): BboxPct | null {
  if (day < 1 || day > 31) return null;
  return {
    topPct: ROW_TOP_PCT + (day - 1) * ROW_HEIGHT_PCT,
    leftPct: 14.0,
    widthPct: 56.0 - 14.0,
    heightPct: ROW_HEIGHT_PCT,
  };
}

/** Backend-detected grid (cv2 line-detection on preview). When present,
 *  caller should prefer this over `cellBboxPct` for pixel-accurate placement.
 *
 *  Backend returns 0-1 fractions; we expose 0-100 percentages here so the
 *  rest of the codebase keeps using the same units as the heuristic. */
export interface DetectedGrid {
  rows: Array<{ day: number; topPct: number; bottomPct: number }>;
  cols: Partial<Record<FieldName, { leftPct: number; rightPct: number }>>;
}

export function detectedCellBbox(
  day: number,
  field: FieldName,
  grid: DetectedGrid | null,
): BboxPct | null {
  if (!grid) return null;
  const row = grid.rows.find((r) => r.day === day);
  const col = grid.cols[field];
  if (!row || !col) return null;
  // Backend returns fractions (0-1); convert to percentages (0-100).
  const topPct = row.topPct * 100;
  const bottomPct = row.bottomPct * 100;
  const leftPct = col.leftPct * 100;
  const rightPct = col.rightPct * 100;
  return {
    topPct,
    leftPct,
    widthPct: rightPct - leftPct,
    heightPct: bottomPct - topPct,
  };
}
